/**
 * useTrelloExportState — manages Trello-specific selection state for export.
 *
 * Handles:
 * - Board selection
 * - List selection (cascade: loads when board changes)
 * - Data loading callback for useExportOAuth
 *
 * Mirrors useAsanaExportState (Workspace→Project), adapted for Trello's
 * Board→List hierarchy. Trello has no field-schema picker in v1.
 */
import { useState, useCallback, useEffect } from 'react';
import { toUserFacingMessage } from '../../../userFacingError.js';
export function useTrelloExportState(runtime) {
    const { api: integrationsApi, debug } = runtime;
    const [trelloBoards, setTrelloBoards] = useState([]);
    const [selectedBoardId, setSelectedBoardId] = useState('');
    const [trelloLists, setTrelloLists] = useState([]);
    const [selectedListId, setSelectedListId] = useState('');
    const [listsLoading, setListsLoading] = useState(false);
    // A failed lists fetch used to clear the lists and say nothing, leaving an
    // empty List select and a permanently disabled Export button (EP-099).
    const [listsError, setListsError] = useState(null);
    const [listsReloadNonce, setListsReloadNonce] = useState(0);
    // The board `trelloLists` was fetched for, '' while a fetch is in flight, so
    // `listsEmpty` means a fetch for THIS board landed empty. `!listsLoading`
    // alone is also true in the render that selects a board, before the fetch
    // effect sets loading.
    const [listsBoardId, setListsBoardId] = useState('');
    /** Callback for useExportOAuth to populate Trello data after connect */
    const onTrelloDataLoaded = useCallback((boards) => {
        debug.log('Trello boards loaded:', boards.length);
        setTrelloBoards(boards);
        if (boards.length > 0) {
            setSelectedBoardId(boards[0].id);
        }
    }, [runtime]);
    /** Load lists when selected board changes */
    useEffect(() => {
        if (!selectedBoardId) {
            setTrelloLists([]);
            setSelectedListId('');
            setListsError(null);
            return;
        }
        let cancelled = false;
        setListsLoading(true);
        setListsError(null);
        setListsBoardId('');
        integrationsApi.getTrelloLists(selectedBoardId)
            .then((lists) => {
            if (cancelled)
                return;
            debug.log('Trello lists loaded:', lists.length);
            setTrelloLists(lists);
            if (lists.length > 0) {
                setSelectedListId(lists[0].id);
            }
            else {
                setSelectedListId('');
            }
            setListsError(null);
            setListsBoardId(selectedBoardId);
            setListsLoading(false);
        })
            .catch((err) => {
            if (cancelled)
                return;
            debug.error('Failed to load Trello lists:', err);
            setTrelloLists([]);
            setSelectedListId('');
            setListsError(toUserFacingMessage(err, "Couldn't load lists for this board."));
            setListsLoading(false);
        });
        return () => { cancelled = true; };
    }, [selectedBoardId, listsReloadNonce, runtime]);
    /** Refetch the lists for the current board after a failure. */
    const retryLoadLists = useCallback(() => {
        setListsReloadNonce((n) => n + 1);
    }, [runtime]);
    /** Reset Trello-specific state */
    const resetTrelloState = useCallback(() => {
        debug.log('Resetting Trello state');
        setSelectedBoardId('');
        setTrelloLists([]);
        setSelectedListId('');
        setListsError(null);
        setListsBoardId('');
    }, [runtime]);
    return {
        trelloBoards,
        selectedBoardId,
        setSelectedBoardId,
        trelloLists,
        selectedListId,
        setSelectedListId,
        listsLoading,
        listsError,
        listsEmpty: !!selectedBoardId && listsBoardId === selectedBoardId && trelloLists.length === 0,
        retryLoadLists,
        onTrelloDataLoaded,
        resetTrelloState,
    };
}
//# sourceMappingURL=useTrelloExportState.js.map